import { useEffect, useMemo, useState } from "react";
import { adminApi, publicApi, BRAND } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, Trash2, Upload, Lock, LogOut, ArrowLeft, Star,
  Image as ImageIcon, Users, Sparkles, Phone, Mail, X,
  BadgeCheck, CalendarClock, Edit3, Key, Copy, ShieldOff, ShieldCheck,
} from "lucide-react";

const CATEGORIES = ["Exterior", "Interior", "Other"];
const STAGES = [
  { value: "", label: "— Showcase —" },
  { value: "before", label: "Before" },
  { value: "after", label: "After" },
];
const LEAD_STATUSES = ["new", "contacted", "scheduled", "completed"];

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem("rrr_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [tab, setTab] = useState("photos");
  const [photos, setPhotos] = useState([]);
  const [leads, setLeads] = useState([]);
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({ photos: 0, leads: 0, new_leads: 0, featured: 0, posts: 0, published_posts: 0 });
  const [editing, setEditing] = useState(null); // photo being edited

  const isFriday = new Date().getDay() === 5;

  const login = async (e) => {
    e?.preventDefault?.();
    setChecking(true);
    try {
      await adminApi(key).post("/admin/verify");
      localStorage.setItem("rrr_admin_key", key);
      setAuthed(true);
      toast.success("Signed in.");
    } catch {
      toast.error("Invalid admin key.");
    } finally {
      setChecking(false);
    }
  };
  const logout = () => {
    localStorage.removeItem("rrr_admin_key");
    setKey(""); setAuthed(false);
  };

  const loadAll = async () => {
    try {
      const [p, l, s, b] = await Promise.all([
        publicApi.get("/photos"),
        adminApi(key).get("/admin/leads"),
        adminApi(key).get("/admin/stats"),
        adminApi(key).get("/admin/blog"),
      ]);
      setPhotos(p.data); setLeads(l.data); setStats(s.data); setPosts(b.data);
    } catch { toast.error("Failed to load data"); }
  };

  useEffect(() => { if (key) login({}); }, []); // eslint-disable-line
  useEffect(() => { if (authed) loadAll(); }, [authed]); // eslint-disable-line

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-md border border-brand-border bg-brand-surface p-10">
          <div className="flex items-center gap-3 mb-8">
            <Lock className="h-6 w-6 text-brand-blue" />
            <h1 className="font-display uppercase text-3xl">Admin Access</h1>
          </div>
          <label className="block text-[10px] tracking-[0.3em] uppercase text-brand-muted mb-2">Admin Key</label>
          <input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Enter admin key"
            data-testid="admin-key-input"
            className="w-full bg-brand-bg border border-brand-border focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none text-white placeholder:text-zinc-600 px-4 py-3" />
          <button type="submit" disabled={checking} data-testid="admin-signin-btn"
            className="mt-6 w-full bg-brand-blue hover:bg-brand-blueHover text-white font-display uppercase tracking-widest py-3 flex items-center justify-center gap-2">
            {checking && <Loader2 className="h-4 w-4 animate-spin" />} Sign In
          </button>
          <a href="/" className="mt-6 inline-flex items-center gap-2 text-brand-muted text-sm hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 md:px-6" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <img src={BRAND.logo} className="h-12 w-12" alt="logo" />
            <div>
              <h1 className="font-display uppercase text-2xl md:text-3xl leading-none">Control Center</h1>
              <p className="text-brand-muted text-xs uppercase tracking-widest">RRR Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-brand-muted hover:text-white text-sm">View site →</a>
            <button onClick={logout} data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 border border-brand-border px-3 py-2 text-sm hover:border-brand-blue">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>

        {/* Friday banner */}
        {isFriday && (
          <div data-testid="friday-banner"
            className="mb-6 flex items-center gap-4 border border-brand-blue bg-brand-blue/10 p-4 md:p-5">
            <CalendarClock className="h-6 w-6 text-brand-blue shrink-0" />
            <div className="flex-1">
              <div className="font-display uppercase text-lg text-white">It&apos;s Friday — pick this week&apos;s featured project.</div>
              <p className="text-brand-muted text-sm mt-1">
                Star a photo below to feature it. Featured photos pin to the top of the homepage gallery.
              </p>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Stat icon={ImageIcon} label="Photos" value={stats.photos} />
          <Stat icon={Star} label="Featured" value={stats.featured} accent />
          <Stat icon={Users} label="Total leads" value={stats.leads} />
          <Stat icon={BadgeCheck} label="New leads" value={stats.new_leads} accent />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-brand-border mb-6 overflow-x-auto" role="tablist">
          {[
            { id: "photos", label: `Photos (${stats.photos})` },
            { id: "leads", label: `Leads (${stats.new_leads} new)` },
            { id: "blog", label: `Blog (${stats.posts})` },
            { id: "keys", label: "Access keys" },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`whitespace-nowrap px-5 py-3 text-[11px] uppercase tracking-[0.28em] transition-colors ${
                tab === t.id ? "text-white border-b-2 border-brand-blue -mb-px" : "text-brand-muted hover:text-white"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "photos" && (
          <PhotosTab
            photos={photos} akey={key} onChange={loadAll}
            editing={editing} setEditing={setEditing}
          />
        )}
        {tab === "leads" && <LeadsTab leads={leads} akey={key} onChange={loadAll} />}
        {tab === "blog" && <BlogTab posts={posts} akey={key} onChange={loadAll} />}
        {tab === "keys" && <ApiKeysTab akey={key} />}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`border border-brand-border p-4 ${accent ? "bg-brand-blue/10" : "bg-brand-surface"}`}>
      <div className="flex items-center gap-2 text-brand-muted text-[10px] tracking-[0.28em] uppercase">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </div>
  );
}

/* ================= PHOTOS TAB ================= */
function PhotosTab({ photos, akey, onChange, editing, setEditing }) {
  const [queue, setQueue] = useState([]); // [{file, preview, ...tagFields}]
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("All");

  const readAsDataUrl = (file) =>
    new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    if (files.some((f) => f.size > 5 * 1024 * 1024)) {
      toast.error("Each image must be under 5MB.");
      return;
    }
    setAnalyzing(true);
    const analyzePromises = files.map(async (f) => {
      const preview = await readAsDataUrl(f);
      const base = {
        file: f, preview,
        title: "", category: "Exterior", stage: "", project: "", caption: "",
        aiFilled: false, aiError: null,
      };
      try {
        const r = await adminApi(akey).post("/admin/photos/analyze", { image_base64: preview });
        const t = r.data;
        Object.assign(base, {
          title: t.title || "",
          category: t.category || "Exterior",
          stage: t.stage || "",
          caption: t.caption || "",
          aiFilled: true,
        });
      } catch (err) {
        base.aiError = "AI tag failed — fill manually";
      }
      return base;
    });
    const items = await Promise.all(analyzePromises);
    setQueue((q) => [...q, ...items]);
    setAnalyzing(false);
    const tagged = items.filter((i) => i.aiFilled).length;
    if (tagged) toast.success(`AI-tagged ${tagged} photo${tagged === 1 ? "" : "s"}.`);
    e.target.value = "";
  };

  const updateQueue = (idx, patch) =>
    setQueue((q) => q.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const removeQueued = (idx) => setQueue((q) => q.filter((_, i) => i !== idx));

  const uploadAll = async () => {
    if (!queue.length) return;
    setUploading(true);
    let ok = 0, dup = 0, fail = 0;
    for (const it of queue) {
      try {
        await adminApi(akey).post("/admin/photos", {
          title: it.title, category: it.category, stage: it.stage || null,
          project: it.project || null, caption: it.caption, image_base64: it.preview,
        });
        ok++;
      } catch (err) {
        if (err?.response?.status === 409) dup++;
        else fail++;
      }
    }
    setUploading(false);
    setQueue([]);
    onChange();
    if (ok) toast.success(`Uploaded ${ok} photo${ok === 1 ? "" : "s"}.`);
    if (dup) toast.info(`${dup} skipped (duplicate).`);
    if (fail) toast.error(`${fail} failed.`);
  };

  const remove = async (id) => {
    if (!confirm("Delete this photo?")) return;
    try { await adminApi(akey).delete(`/admin/photos/${id}`); toast.success("Deleted."); onChange(); }
    catch { toast.error("Delete failed."); }
  };

  const toggleFeatured = async (p) => {
    try {
      await adminApi(akey).patch(`/admin/photos/${p.id}`, { featured: !p.featured });
      toast.success(!p.featured ? "Featured on homepage." : "Unfeatured.");
      onChange();
    } catch { toast.error("Update failed."); }
  };

  const categoriesPresent = useMemo(
    () => Array.from(new Set(photos.map((p) => p.category))),
    [photos]
  );
  const visible = filter === "All" ? photos : photos.filter((p) => p.category === filter);

  return (
    <div>
      {/* Upload dropzone */}
      <div className="border border-brand-border bg-brand-surface p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="font-display uppercase text-xl">Upload photos</h3>
            <p className="text-brand-muted text-xs mt-1 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-brand-blue" />
              AI will auto-fill category, stage, title & caption. Duplicates are auto-blocked.
            </p>
          </div>
          <label
            data-testid="upload-file-label"
            className={`inline-flex items-center gap-2 border border-brand-border hover:border-brand-blue cursor-pointer px-4 py-3 text-sm ${analyzing ? "opacity-60 pointer-events-none" : ""}`}
          >
            {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {analyzing ? "Tagging..." : "Choose photos"}
            <input type="file" multiple accept="image/*" onChange={onFiles} className="hidden" data-testid="upload-file-input" />
          </label>
        </div>

        {queue.length > 0 && (
          <div data-testid="upload-queue" className="space-y-3 mt-4">
            {queue.map((it, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-[120px,1fr,auto] gap-4 border border-brand-border p-3 bg-brand-bg">
                <img src={it.preview} alt="" className="h-24 w-full md:w-28 object-cover" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <input value={it.title} onChange={(e) => updateQueue(i, { title: e.target.value })}
                    placeholder="Title" className="col-span-2 bg-brand-surface border border-brand-border px-3 py-2 text-sm" />
                  <select value={it.category} onChange={(e) => updateQueue(i, { category: e.target.value })}
                    className="bg-brand-surface border border-brand-border px-3 py-2 text-sm">
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <select value={it.stage} onChange={(e) => updateQueue(i, { stage: e.target.value })}
                    className="bg-brand-surface border border-brand-border px-3 py-2 text-sm">
                    {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <input value={it.project} onChange={(e) => updateQueue(i, { project: e.target.value })}
                    placeholder="Project (optional, pairs before/after)" className="col-span-2 bg-brand-surface border border-brand-border px-3 py-2 text-sm" />
                  <input value={it.caption} onChange={(e) => updateQueue(i, { caption: e.target.value })}
                    placeholder="Caption" className="col-span-2 bg-brand-surface border border-brand-border px-3 py-2 text-sm" />
                  <div className="col-span-2 md:col-span-4 flex items-center gap-2 text-xs">
                    {it.aiFilled && (
                      <span className="inline-flex items-center gap-1 text-brand-blue">
                        <Sparkles className="h-3 w-3" /> AI-tagged
                      </span>
                    )}
                    {it.aiError && <span className="text-yellow-400">{it.aiError}</span>}
                  </div>
                </div>
                <button onClick={() => removeQueued(i)} className="text-brand-muted hover:text-white" data-testid={`queue-remove-${i}`}>
                  <X className="h-5 w-5" />
                </button>
              </div>
            ))}
            <button onClick={uploadAll} disabled={uploading}
              data-testid="upload-all-btn"
              className="w-full bg-brand-blue hover:bg-brand-blueHover text-white font-display uppercase tracking-widest py-3 flex items-center justify-center gap-2">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload {queue.length} photo{queue.length === 1 ? "" : "s"}
            </button>
          </div>
        )}
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {["All", ...categoriesPresent].map((c) => (
          <button key={c} onClick={() => setFilter(c)}
            className={`px-4 py-2 text-[10px] tracking-[0.3em] uppercase border ${
              filter === c ? "bg-brand-blue border-brand-blue text-white" : "border-brand-border text-brand-muted hover:text-white hover:border-white"
            }`}>
            {c} <span className="ml-1 opacity-60">{c === "All" ? photos.length : photos.filter((p) => p.category === c).length}</span>
          </button>
        ))}
      </div>

      {/* Photo grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photos-grid">
        {visible.map((p) => (
          <div key={p.id} className="relative group border border-brand-border bg-brand-surface">
            <img src={p.image_base64}
              style={p.rotation ? { transform: `rotate(${p.rotation}deg) scale(1.6)` } : undefined}
              alt={p.title} className="w-full h-40 md:h-48 object-cover" />
            {p.featured && (
              <div className="absolute top-2 left-2 bg-brand-blue text-white text-[9px] tracking-widest uppercase px-2 py-1 flex items-center gap-1">
                <Star className="h-3 w-3 fill-white" /> Featured
              </div>
            )}
            {p.stage && (
              <div className={`absolute top-2 right-2 text-[9px] tracking-widest uppercase px-2 py-1 ${p.stage === "after" ? "bg-brand-blue text-white" : "bg-white text-brand-bg"}`}>
                {p.stage}
              </div>
            )}
            <div className="absolute inset-0 bg-brand-bg/0 md:group-hover:bg-brand-bg/80 transition-colors flex items-end p-3">
              <div className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity w-full space-y-2">
                <div className="hidden md:block">
                  <div className="text-xs text-white truncate">{p.title || "Untitled"}</div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-blue">{p.category}{p.project ? ` · ${p.project}` : ""}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => toggleFeatured(p)} title="Toggle featured"
                    data-testid={`feature-${p.id}`}
                    className={`flex-1 inline-flex items-center justify-center gap-1 border px-2 py-1.5 text-xs ${p.featured ? "bg-brand-blue text-white border-brand-blue" : "border-white/30 text-white hover:bg-white hover:text-black"}`}>
                    <Star className={`h-3 w-3 ${p.featured ? "fill-white" : ""}`} />
                    {p.featured ? "Featured" : "Feature"}
                  </button>
                  <button onClick={() => setEditing(p)} title="Edit"
                    data-testid={`edit-${p.id}`}
                    className="inline-flex items-center gap-1 border border-white/30 px-2 py-1.5 text-xs hover:bg-white hover:text-black">
                    <Edit3 className="h-3 w-3" /> Edit
                  </button>
                  <button onClick={() => remove(p.id)} title="Delete"
                    data-testid={`delete-photo-${p.id}`}
                    className="inline-flex items-center gap-1 border border-white/30 px-2 py-1.5 text-xs hover:bg-red-600 hover:border-red-600 hover:text-white">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditPhotoModal photo={editing} akey={akey} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChange(); }} />
      )}
    </div>
  );
}

function EditPhotoModal({ photo, akey, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: photo.title || "",
    category: photo.category || "Exterior",
    stage: photo.stage || "",
    project: photo.project || "",
    caption: photo.caption || "",
    rotation: photo.rotation || 0,
    featured: !!photo.featured,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi(akey).patch(`/admin/photos/${photo.id}`, {
        ...form, stage: form.stage || null, project: form.project || null,
      });
      toast.success("Saved.");
      onSaved();
    } catch { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-bg/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        data-testid="edit-photo-modal"
        className="bg-brand-surface border border-brand-border max-w-2xl w-full max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between p-5 border-b border-brand-border">
          <h3 className="font-display uppercase text-xl">Edit photo</h3>
          <button onClick={onClose} className="text-brand-muted hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <img src={photo.image_base64}
            style={form.rotation ? { transform: `rotate(${form.rotation}deg) scale(1.4)` } : undefined}
            alt="" className="w-full h-56 object-cover border border-brand-border" />
          <div className="grid grid-cols-2 gap-3">
            <Labeled label="Title" full>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full bg-brand-bg border border-brand-border px-3 py-2" />
            </Labeled>
            <Labeled label="Category">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full bg-brand-bg border border-brand-border px-3 py-2">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Labeled>
            <Labeled label="Stage">
              <select value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}
                className="w-full bg-brand-bg border border-brand-border px-3 py-2">
                {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Labeled>
            <Labeled label="Project" full>
              <input value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })}
                className="w-full bg-brand-bg border border-brand-border px-3 py-2" />
            </Labeled>
            <Labeled label="Caption" full>
              <input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })}
                className="w-full bg-brand-bg border border-brand-border px-3 py-2" />
            </Labeled>
            <Labeled label="Rotation (deg)">
              <select value={form.rotation} onChange={(e) => setForm({ ...form, rotation: Number(e.target.value) })}
                className="w-full bg-brand-bg border border-brand-border px-3 py-2">
                {[0, 90, 180, 270].map((r) => <option key={r} value={r}>{r}°</option>)}
              </select>
            </Labeled>
            <Labeled label="Featured on homepage">
              <button type="button" onClick={() => setForm({ ...form, featured: !form.featured })}
                data-testid="modal-toggle-featured"
                className={`inline-flex items-center gap-2 px-3 py-2 border ${form.featured ? "bg-brand-blue border-brand-blue text-white" : "border-brand-border text-brand-muted"}`}>
                <Star className={`h-4 w-4 ${form.featured ? "fill-white" : ""}`} /> {form.featured ? "Featured" : "Not featured"}
              </button>
            </Labeled>
          </div>
          <button onClick={save} disabled={saving} data-testid="save-photo-btn"
            className="w-full bg-brand-blue hover:bg-brand-blueHover text-white font-display uppercase tracking-widest py-3 flex items-center justify-center gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Labeled({ label, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-[10px] tracking-[0.28em] uppercase text-brand-muted mb-1.5">{label}</label>
      {children}
    </div>
  );
}

/* ================= LEADS TAB ================= */
function LeadsTab({ leads, akey, onChange }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const filtered = statusFilter === "all" ? leads : leads.filter((l) => (l.status || "new") === statusFilter);
  const counts = LEAD_STATUSES.reduce((acc, s) => ({ ...acc, [s]: leads.filter((l) => (l.status || "new") === s).length }), {});

  const setStatus = async (l, status) => {
    try {
      await adminApi(akey).patch(`/admin/leads/${l.id}`, { status });
      toast.success(status === "contacted" ? "Marked as contacted." : `Marked ${status}.`);
      onChange();
    } catch { toast.error("Update failed."); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <FilterChip active={statusFilter === "all"} onClick={() => setStatusFilter("all")} label="All" count={leads.length} />
        {LEAD_STATUSES.map((s) => (
          <FilterChip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)} label={s} count={counts[s]} />
        ))}
      </div>

      <div className="border border-brand-border overflow-x-auto" data-testid="leads-table">
        <table className="w-full text-sm">
          <thead className="bg-brand-surface text-brand-muted uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Phone</th>
              <th className="text-left px-4 py-3">Service</th>
              <th className="text-left px-4 py-3">Notes</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-brand-muted">No leads in this view.</td></tr>
            )}
            {filtered.map((l) => (
              <tr key={l.id} className="border-t border-brand-border align-top">
                <td className="px-4 py-3"><StatusPill status={l.status || "new"} /></td>
                <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{l.name}</div>
                  <div className="text-brand-muted text-xs">{l.email || ""}</div>
                </td>
                <td className="px-4 py-3 space-y-1">
                  <a href={`tel:${l.phone}`} className="text-brand-blue font-medium flex items-center gap-1"><Phone className="h-3 w-3" /> {l.phone}</a>
                  {l.email && <a href={`mailto:${l.email}`} className="text-brand-muted text-xs flex items-center gap-1"><Mail className="h-3 w-3" /> Email</a>}
                </td>
                <td className="px-4 py-3">
                  <div>{l.service}</div>
                  <div className="text-brand-muted text-xs">{l.property_type}</div>
                </td>
                <td className="px-4 py-3 max-w-xs">{l.message || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    {LEAD_STATUSES.filter((s) => s !== (l.status || "new")).map((s) => (
                      <button key={s} onClick={() => setStatus(l, s)}
                        data-testid={`lead-${l.id}-${s}`}
                        className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${s === "contacted" ? "border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white" : "border-brand-border text-brand-muted hover:border-white hover:text-white"}`}>
                        {s === "contacted" ? "Mark contacted" : `Mark ${s}`}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, label, count }) {
  return (
    <button onClick={onClick}
      className={`px-4 py-2 text-[10px] tracking-[0.3em] uppercase border ${active ? "bg-brand-blue border-brand-blue text-white" : "border-brand-border text-brand-muted hover:text-white hover:border-white"}`}>
      {label} <span className="ml-1 opacity-60">{count}</span>
    </button>
  );
}

function StatusPill({ status }) {
  const color = {
    new: "bg-brand-blue/20 text-brand-blue border-brand-blue",
    contacted: "bg-white/10 text-white border-white/40",
    scheduled: "bg-yellow-500/10 text-yellow-400 border-yellow-500/50",
    completed: "bg-green-500/10 text-green-400 border-green-500/50",
  }[status] || "border-brand-border text-brand-muted";
  return <span className={`inline-block text-[9px] tracking-widest uppercase px-2 py-1 border ${color}`}>{status}</span>;
}


/* ================= BLOG TAB ================= */
function BlogTab({ posts, akey, onChange }) {
  const [editing, setEditing] = useState(null); // post or "new"
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display uppercase text-xl">Painting Tips</h3>
          <p className="text-brand-muted text-xs mt-1">
            Write expert advice posts. Published posts appear at rrrpaint.com/blog.
          </p>
        </div>
        <button
          onClick={() => setEditing("new")}
          data-testid="new-post-btn"
          className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blueHover text-white px-5 py-3 font-display uppercase tracking-widest text-sm"
        >
          <Sparkles className="h-4 w-4" /> New post
        </button>
      </div>

      <div className="border border-brand-border overflow-x-auto" data-testid="blog-table">
        <table className="w-full text-sm">
          <thead className="bg-brand-surface text-brand-muted uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Slug</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-brand-muted">No posts yet. Click &ldquo;New post&rdquo; to write your first painting tip.</td></tr>
            )}
            {posts.map((p) => (
              <tr key={p.id} className="border-t border-brand-border">
                <td className="px-4 py-3 font-medium">{p.title}</td>
                <td className="px-4 py-3 text-brand-muted"><code className="text-xs">{p.slug}</code></td>
                <td className="px-4 py-3">
                  <span className={`text-[9px] tracking-widest uppercase px-2 py-1 border ${p.published ? "bg-brand-blue/20 text-brand-blue border-brand-blue" : "border-brand-border text-brand-muted"}`}>
                    {p.published ? "Published" : "Draft"}
                  </span>
                </td>
                <td className="px-4 py-3 text-brand-muted whitespace-nowrap">{new Date(p.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer"
                       className="text-[10px] tracking-widest uppercase px-2 py-1 border border-brand-border hover:border-white hover:text-white text-brand-muted">
                      View
                    </a>
                    <button onClick={() => setEditing(p)}
                      data-testid={`edit-post-${p.id}`}
                      className="text-[10px] tracking-widest uppercase px-2 py-1 border border-brand-border hover:border-brand-blue hover:text-white text-brand-muted">
                      Edit
                    </button>
                    <button onClick={async () => {
                      if (!confirm("Delete this post?")) return;
                      try {
                        await adminApi(akey).delete(`/admin/blog/${p.id}`);
                        toast.success("Deleted."); onChange();
                      } catch { toast.error("Delete failed."); }
                    }}
                      data-testid={`delete-post-${p.id}`}
                      className="text-[10px] tracking-widest uppercase px-2 py-1 border border-brand-border hover:border-red-500 hover:text-red-400 text-brand-muted">
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <PostEditor
          initial={editing === "new" ? null : editing}
          akey={akey}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChange(); }}
        />
      )}
    </div>
  );
}

function PostEditor({ initial, akey, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    excerpt: initial?.excerpt || "",
    content: initial?.content || "",
    cover_image: initial?.cover_image || "",
    tags: (initial?.tags || []).join(", "),
    author: initial?.author || "RRR Paint & Construction",
    published: initial?.published ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const readAsDataUrl = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const onCoverFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Choose an image file.");
    if (f.size > 5 * 1024 * 1024) return toast.error("Cover image must be under 5MB.");
    setUploadingCover(true);
    try {
      const b64 = await readAsDataUrl(f);
      setForm((s) => ({ ...s, cover_image: b64 }));
    } finally { setUploadingCover(false); }
  };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        excerpt: form.excerpt,
        content: form.content,
        cover_image: form.cover_image,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        author: form.author,
        published: !!form.published,
      };
      if (initial?.id) {
        await adminApi(akey).patch(`/admin/blog/${initial.id}`, payload);
      } else {
        await adminApi(akey).post("/admin/blog", payload);
      }
      toast.success(initial?.id ? "Post updated." : "Post published!");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-bg/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        data-testid="post-editor"
        className="bg-brand-surface border border-brand-border max-w-3xl w-full my-8 md:my-0 max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between p-5 border-b border-brand-border sticky top-0 bg-brand-surface z-10">
          <h3 className="font-display uppercase text-xl">
            {initial?.id ? "Edit post" : "New post"}
          </h3>
          <button onClick={onClose} className="text-brand-muted hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <Labeled label="Title" full>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. How to Prep Exterior Siding Before Painting"
              data-testid="post-title"
              className="w-full bg-brand-bg border border-brand-border px-3 py-2" />
          </Labeled>
          <Labeled label="Excerpt (1-2 sentence summary)" full>
            <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              rows={2}
              data-testid="post-excerpt"
              className="w-full bg-brand-bg border border-brand-border px-3 py-2" />
          </Labeled>

          <Labeled label="Cover image" full>
            {form.cover_image && (
              <img src={form.cover_image} alt="" className="mb-3 w-full h-40 object-cover border border-brand-border" />
            )}
            <div className="flex gap-2">
              <label className="cursor-pointer inline-flex items-center gap-2 border border-brand-border hover:border-brand-blue px-4 py-2 text-sm">
                {uploadingCover ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {form.cover_image ? "Change" : "Upload cover"}
                <input type="file" accept="image/*" className="hidden" onChange={onCoverFile} />
              </label>
              {form.cover_image && (
                <button onClick={() => setForm({ ...form, cover_image: "" })}
                  className="border border-brand-border hover:border-red-500 hover:text-red-400 px-4 py-2 text-sm">
                  Remove
                </button>
              )}
            </div>
          </Labeled>

          <Labeled label="Content (Markdown supported: # headings, **bold**, - lists, [link](url), > quote)" full>
            <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={14}
              data-testid="post-content"
              placeholder={`## Prep is 80% of the job

Before you open a can of paint...

**Pro tip:** always...`}
              className="w-full bg-brand-bg border border-brand-border px-3 py-2 font-mono text-sm leading-relaxed" />
          </Labeled>

          <div className="grid grid-cols-2 gap-3">
            <Labeled label="Tags (comma-separated)">
              <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="exterior, prep, tips"
                className="w-full bg-brand-bg border border-brand-border px-3 py-2" />
            </Labeled>
            <Labeled label="Author">
              <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })}
                className="w-full bg-brand-bg border border-brand-border px-3 py-2" />
            </Labeled>
          </div>

          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })}
                data-testid="post-published" />
              <span className="text-sm">Published (visible on rrrpaint.com/blog)</span>
            </label>
          </div>

          <button onClick={save} disabled={saving}
            data-testid="save-post-btn"
            className="w-full bg-brand-blue hover:bg-brand-blueHover text-white font-display uppercase tracking-widest py-3 flex items-center justify-center gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {initial?.id ? "Save changes" : "Publish post"}
          </button>
        </div>
      </div>
    </div>
  );

}

/* ================= API KEYS TAB ================= */
function ApiKeysTab({ akey }) {
  const [keys, setKeys] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState(null); // just-created key (shown once)
  const [form, setForm] = useState({ label: "", scopes: [] });
  const [editing, setEditing] = useState(null); // key being edited

  const load = async () => {
    try {
      const [k, s] = await Promise.all([
        adminApi(akey).get("/admin/api-keys"),
        adminApi(akey).get("/admin/available-scopes"),
      ]);
      setKeys(k.data); setScopes(s.data.scopes);
    } catch { toast.error("Failed to load API keys."); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleScope = (s) =>
    setForm((f) => ({ ...f, scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s] }));

  const create = async () => {
    if (!form.label.trim()) return toast.error("Label required.");
    if (!form.scopes.length) return toast.error("Pick at least one scope.");
    setCreating(true);
    try {
      const r = await adminApi(akey).post("/admin/api-keys", form);
      setNewKey(r.data);
      setForm({ label: "", scopes: [] });
      setShowCreate(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed.");
    } finally { setCreating(false); }
  };

  const toggleRevoke = async (k) => {
    try {
      await adminApi(akey).patch(`/admin/api-keys/${k.id}`, { revoked: !k.revoked });
      toast.success(k.revoked ? "Key re-enabled." : "Key revoked.");
      load();
    } catch { toast.error("Update failed."); }
  };

  const remove = async (k) => {
    if (!confirm(`Delete "${k.label}"? This can't be undone.`)) return;
    try { await adminApi(akey).delete(`/admin/api-keys/${k.id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied to clipboard."),
      () => toast.error("Copy failed — select and copy manually.")
    );
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h3 className="font-display uppercase text-xl flex items-center gap-2"><Key className="h-5 w-5 text-brand-blue" /> Access keys</h3>
          <p className="text-brand-muted text-xs mt-1 max-w-xl">
            Scoped keys for third-party tools (like Wingman). Each key only has the permissions you grant.
            Revoke or delete anytime.
          </p>
        </div>
        <button onClick={() => setShowCreate((v) => !v)}
          data-testid="new-key-btn"
          className="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blueHover text-white px-5 py-3 font-display uppercase tracking-widest text-sm">
          <Sparkles className="h-4 w-4" /> New key
        </button>
      </div>

      {/* New-key form */}
      {showCreate && (
        <div className="border border-brand-border bg-brand-surface p-5 mb-4 space-y-4" data-testid="new-key-form">
          <Labeled label="Label (what/who is this key for?)" full>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Wingman"
              data-testid="new-key-label"
              className="w-full bg-brand-bg border border-brand-border px-3 py-2" />
          </Labeled>
          <div>
            <label className="block text-[10px] tracking-[0.28em] uppercase text-brand-muted mb-2">Scopes (what this key can do)</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {scopes.map((s) => (
                <label key={s} className={`inline-flex items-center gap-2 border cursor-pointer px-3 py-2 text-sm ${form.scopes.includes(s) ? "border-brand-blue bg-brand-blue/10 text-white" : "border-brand-border text-brand-muted hover:border-white hover:text-white"}`}>
                  <input type="checkbox" checked={form.scopes.includes(s)} onChange={() => toggleScope(s)} data-testid={`scope-${s}`} />
                  <code className="text-xs">{s}</code>
                </label>
              ))}
            </div>
          </div>
          <button onClick={create} disabled={creating}
            data-testid="create-key-btn"
            className="w-full bg-brand-blue hover:bg-brand-blueHover text-white font-display uppercase tracking-widest py-3 flex items-center justify-center gap-2">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Create key
          </button>
        </div>
      )}

      {/* Just-created key display (shown ONCE) */}
      {newKey && (
        <div className="border-2 border-brand-blue bg-brand-blue/10 p-5 mb-4 space-y-3" data-testid="new-key-reveal">
          <div className="flex items-center gap-2 text-brand-blue">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-[10px] tracking-[0.3em] uppercase font-semibold">Save this key now — you won&apos;t see it again</span>
          </div>
          <div className="font-display uppercase text-xl">{newKey.label}</div>
          <div className="flex items-center gap-2 bg-brand-bg border border-brand-border p-3">
            <code className="flex-1 text-sm break-all text-white select-all" data-testid="revealed-key">{newKey.key}</code>
            <button onClick={() => copy(newKey.key)}
              data-testid="copy-key-btn"
              className="inline-flex items-center gap-1 bg-brand-blue hover:bg-brand-blueHover text-white px-3 py-2 text-xs uppercase tracking-widest">
              <Copy className="h-3 w-3" /> Copy
            </button>
          </div>
          <div className="text-xs text-brand-muted">
            Scopes: {newKey.scopes.map((s) => <code key={s} className="bg-brand-bg border border-brand-border px-1.5 py-0.5 mr-1">{s}</code>)}
          </div>
          <div className="flex items-start gap-2 bg-brand-surface border border-brand-border p-3 text-xs text-brand-muted">
            <span>
              <strong className="text-white">How to use:</strong> Send it as an <code className="text-brand-blue">X-API-Key</code> header on API requests. Example:
              <pre className="mt-2 whitespace-pre-wrap text-[11px] text-white/80">{`curl -X POST https://rrrpaint.com/api/admin/blog \\
  -H "X-API-Key: ${newKey.key}" \\
  -H "Content-Type: application/json" \\
  -d '{"title":"...","content":"..."}'`}</pre>
            </span>
          </div>
          <button onClick={() => setNewKey(null)}
            className="text-brand-muted hover:text-white text-xs uppercase tracking-widest">
            I&apos;ve saved it — dismiss
          </button>
        </div>
      )}

      {/* Keys table */}
      <div className="border border-brand-border overflow-x-auto" data-testid="keys-table">
        <table className="w-full text-sm">
          <thead className="bg-brand-surface text-brand-muted uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Label</th>
              <th className="text-left px-4 py-3">Prefix</th>
              <th className="text-left px-4 py-3">Scopes</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Last used</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-brand-muted">No keys yet. Create one above for Wingman or any tool.</td></tr>
            )}
            {keys.map((k) => (
              <tr key={k.id} className={`border-t border-brand-border ${k.revoked ? "opacity-50" : ""}`}>
                <td className="px-4 py-3 font-medium">{k.label}</td>
                <td className="px-4 py-3"><code className="text-xs text-brand-blue">{k.key_prefix}…</code></td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(k.scopes || []).map((s) => (
                      <code key={s} className="bg-brand-bg border border-brand-border px-1.5 py-0.5 text-xs">{s}</code>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[9px] tracking-widest uppercase px-2 py-1 border ${k.revoked ? "border-red-500/60 text-red-400" : "border-brand-blue text-brand-blue"}`}>
                    {k.revoked ? "Revoked" : "Active"}
                  </span>
                </td>
                <td className="px-4 py-3 text-brand-muted text-xs">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "Never"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    <button onClick={() => setEditing(k)}
                      data-testid={`edit-key-${k.id}`}
                      className="text-[10px] tracking-widest uppercase px-2 py-1 border border-brand-border hover:border-brand-blue hover:text-white text-brand-muted">
                      <Edit3 className="h-3 w-3 inline" /> Edit scopes
                    </button>
                    <button onClick={() => toggleRevoke(k)}
                      data-testid={`revoke-key-${k.id}`}
                      className={`text-[10px] tracking-widest uppercase px-2 py-1 border ${k.revoked ? "border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white" : "border-brand-border text-brand-muted hover:border-yellow-500 hover:text-yellow-400"}`}>
                      {k.revoked ? <><ShieldCheck className="h-3 w-3 inline" /> Re-enable</> : <><ShieldOff className="h-3 w-3 inline" /> Revoke</>}
                    </button>
                    <button onClick={() => remove(k)}
                      data-testid={`delete-key-${k.id}`}
                      className="text-[10px] tracking-widest uppercase px-2 py-1 border border-brand-border hover:border-red-500 hover:text-red-400 text-brand-muted">
                      <Trash2 className="h-3 w-3 inline" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditScopesModal
          keyRec={editing} scopes={scopes} akey={akey}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EditScopesModal({ keyRec, scopes, akey, onClose, onSaved }) {
  const [label, setLabel] = useState(keyRec.label);
  const [selected, setSelected] = useState(keyRec.scopes || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggle = (s) => setSelected((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi(akey).patch(`/admin/api-keys/${keyRec.id}`, { label, scopes: selected });
      toast.success("Scopes updated.");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-bg/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        data-testid="edit-key-modal"
        className="bg-brand-surface border border-brand-border max-w-lg w-full">
        <div className="flex items-center justify-between p-5 border-b border-brand-border">
          <h3 className="font-display uppercase text-xl">Edit key</h3>
          <button onClick={onClose} className="text-brand-muted hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <Labeled label="Label" full>
            <input value={label} onChange={(e) => setLabel(e.target.value)}
              className="w-full bg-brand-bg border border-brand-border px-3 py-2" />
          </Labeled>
          <div>
            <label className="block text-[10px] tracking-[0.28em] uppercase text-brand-muted mb-2">Scopes</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {scopes.map((s) => (
                <label key={s} className={`inline-flex items-center gap-2 border cursor-pointer px-3 py-2 text-sm ${selected.includes(s) ? "border-brand-blue bg-brand-blue/10 text-white" : "border-brand-border text-brand-muted hover:border-white hover:text-white"}`}>
                  <input type="checkbox" checked={selected.includes(s)} onChange={() => toggle(s)} />
                  <code className="text-xs">{s}</code>
                </label>
              ))}
            </div>
          </div>
          <button onClick={save} disabled={saving}
            data-testid="save-key-btn"
            className="w-full bg-brand-blue hover:bg-brand-blueHover text-white font-display uppercase tracking-widest py-3 flex items-center justify-center gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
          </button>
        </div>
      </div>
    </div>
  );
}
